// Single source of truth for the Additional Quote Details fields: the dropdown option
// lists, the installation cost formulas, and — for the inline editor in the quote table —
// which row maps to which quoteDetails property and what kind of input it needs.
//
// QuoteDetailsModal (the "Additional Quote Details" popup) and the inline EDIT mode in
// tableComponent both read from here so the two can't drift: a dropdown in the modal is a
// dropdown in the table, with the same options and the same "Enter Custom ..." escape hatch.

export const formatText = (text) => {
  if (!text) return '';
  if (text.toLowerCase().includes('fob')) {
    return text.split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()).join(' ');
  }
  return text.split(/[\s_]+/).map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()).join(' ');
};

// Fixed lists — not backed by the global-options API.
export const STATIC_DROPDOWN_OPTIONS = {
  delivery_methods: ['Customer Pickup', 'Ship to Customer', 'Enter Custom Delivery Method'],
  installation_options: ['Customer Installed', 'BABS Installed', 'Enter Custom Installation Option'],
  install_quoted_options: ['Not Quoted', 'Quoted', 'Enter Custom Install Quoted'],
};

// Shape to render against before getGlobalOptions() has answered.
export const EMPTY_DROPDOWN_OPTIONS = {
  colors: [],
  saw_delivery: [],
  conveyor_delivery: [],
  delivery_terms: [],
  inspection: [],
  warranty: [],
  tax: [],
  payment: [],
  location: [],
  ...STATIC_DROPDOWN_OPTIONS,
};

// Builds every dropdown list from one getGlobalOptions() response.
export const buildDropdownOptions = (data) => {
  const warranty = data?.warranty || {};
  const warrantyOptions = [
    `Parts Only: ${warranty?.Parts_only_years || 1} Year(s)`,
    `Parts + Labor: ${warranty?.parts_labor_years || 1} Year(s)`,
  ];

  return {
    ...STATIC_DROPDOWN_OPTIONS,
    colors: ['Enter Custom Color', ...((data?.colors || []).map(formatText))],
    saw_delivery: ['Enter Custom Saw Delivery', ...((data?.saw_delivery || []).map(formatText))],
    conveyor_delivery: ['Enter Custom Conveyor Delivery', ...((data?.conveyor_delivery || []).map(formatText))],
    delivery_terms: ['Enter Custom Delivery Term', ...((data?.babs_incoterms || []).map(formatText))],
    inspection: ['Enter Custom Inspection', ...((data?.inspection_options || []).map(formatText))],
    warranty: ['Enter Custom Warranty', ...warrantyOptions],
    tax: ['Enter Custom Sales Tax', 'Out Of State – No Sales Tax', 'Resale – No Sales Tax', 'Texas Conveyor Only 8.25%'],
    payment: ['Enter Custom Payment Term', ...((data?.payment_terms || []).map(formatText))],
    // No custom entry here on purpose — installation location drives priced logic.
    location: (data?.location_options || []).map(formatText),
  };
};

// Tech/day pick lists come from the installation config doc, comma-separated.
export const getTechOptions = (installationData) =>
  (installationData?.Number_of_Service_Technicians ? installationData.Number_of_Service_Technicians.split(',') : ['0']);

export const getDayOptions = (installationData) =>
  (installationData?.Number_of_Days ? installationData.Number_of_Days.split(',') : ['0']);

// ---- Installation cost formulas -------------------------------------------------
// Kept here (rather than duplicated in the modal and the table) because these feed the
// quote's grand total and the tax line. Mileage is deliberately NOT part of the
// installation total — that has always been the case; don't "fix" it without asking.

export const computeLaborTotal = (installation) => {
  if (!installation?.dayRate) return '0.00';
  const rate = parseFloat(installation.dayRate) || 0;
  const techs = parseInt(installation.techs) || 0;
  const days = parseInt(installation.days) || 0;
  return (techs * rate * days * 8).toFixed(2);
};

export const computeLodgingTotal = (installation) => {
  const rate = parseFloat(installation?.lodgingMeals) || 0;
  const techs = parseInt(installation?.techs) || 0;
  const days = parseInt(installation?.days) || 0;
  return rate * techs * days;
};

export const computeInstallationTotal = (installation) => {
  const num = (v) => parseFloat(v) || 0;
  const labor = parseFloat(computeLaborTotal(installation)) || 0;
  return (
    labor +
    computeLodgingTotal(installation) +
    num(installation?.carRental) +
    num(installation?.toolShipments) +
    num(installation?.airfare) +
    num(installation?.travelTimeCost) +
    num(installation?.conveyorShippingPrice)
  ).toFixed(2);
};

// Editing any of these changes the installation total, so the derived totals get
// recomputed. Editing anything else under `installation` (address, city, ...) leaves a
// stored total alone, so opening a legacy quote and fixing a typo in the address can't
// silently re-price it.
export const INSTALLATION_COST_FIELDS = new Set([
  'techs', 'days', 'dayRate', 'lodgingMeals', 'carRental',
  'toolShipments', 'airfare', 'travelTimeCost', 'conveyorShippingPrice',
]);

// ---- Row id -> editor -----------------------------------------------------------
// Keys match the `id` values built by getAdditionalDetailsItems() in tableComponent.
//   kind 'dropdown' — pick list; `customType` (when set) enables the "Enter Custom ..." prompt
//   kind 'text'     — free text
//   kind 'money'    — numeric, digits and one decimal point only
//   kind 'computed' — derived; shown read-only because editing it would be a lie
// `path` is the location in quoteDetails: ['color'] or ['installation', 'dayRate'].
export const QUOTE_DETAIL_FIELDS = {
  'quote-price': { kind: 'money', path: ['quotePrice'] },
  'customer-rfq': { kind: 'text', path: ['customerRFQ'] },
  // Standard Discount comes from the quote form's discount field, not quoteDetails, and
  // is read-only there too.
  'standard-discount': { kind: 'computed' },

  'delivery-method': { kind: 'dropdown', optionsKey: 'delivery_methods', customType: 'Delivery Method', path: ['installation', 'deliveryMethod'] },
  'installation-option': { kind: 'dropdown', optionsKey: 'installation_options', customType: 'Installation Option', path: ['installation', 'installationOption'] },
  'install-quoted': { kind: 'dropdown', optionsKey: 'install_quoted_options', customType: 'Install Quoted', path: ['installation', 'installQuoted'] },
  'techs': { kind: 'dropdown', optionsKey: 'techs', path: ['installation', 'techs'] },
  'days': { kind: 'dropdown', optionsKey: 'days', path: ['installation', 'days'] },
  'day-rate': { kind: 'money', path: ['installation', 'dayRate'] },
  'labor-total': { kind: 'computed' },
  'airfare': { kind: 'money', path: ['installation', 'airfare'] },
  'car-rental': { kind: 'money', path: ['installation', 'carRental'] },
  // The row displays the lodging TOTAL, but the editable number is the per-tech-per-day
  // rate, so the editor shows the rate and spells the total out underneath it.
  'lodging-meals': { kind: 'money', path: ['installation', 'lodgingMeals'], hint: 'lodging' },
  'conveyor-shipping-price': { kind: 'money', path: ['installation', 'conveyorShippingPrice'] },
  'installation-total': { kind: 'computed' },
  'mileage': { kind: 'money', path: ['installation', 'mileage'] },
  'travel-time-cost': { kind: 'money', path: ['installation', 'travelTimeCost'] },
  'tool-shipments': { kind: 'money', path: ['installation', 'toolShipments'] },
  'address': { kind: 'text', path: ['installation', 'address'] },
  'city': { kind: 'text', path: ['installation', 'city'] },
  'state': { kind: 'text', path: ['installation', 'state'] },
  'zipcode': { kind: 'text', path: ['installation', 'zipcode'] },

  'color': { kind: 'dropdown', optionsKey: 'colors', customType: 'Conveyor Color', path: ['color'] },
  'conveyor-delivery': { kind: 'dropdown', optionsKey: 'conveyor_delivery', customType: 'Conveyor Delivery', path: ['conveyorDelivery'] },
  'delivery-terms': { kind: 'dropdown', optionsKey: 'delivery_terms', customType: 'Delivery Terms', path: ['deliveryTerms'] },
  'saw-delivery': { kind: 'dropdown', optionsKey: 'saw_delivery', customType: 'Saw Delivery', path: ['sawDelivery'] },
  'inspection': { kind: 'dropdown', optionsKey: 'inspection', customType: 'Inspection', path: ['inspection'] },
  'warranty': { kind: 'dropdown', optionsKey: 'warranty', customType: 'Warranty', path: ['warranty'] },
  'tax': { kind: 'dropdown', optionsKey: 'tax', customType: 'Sales Tax', path: ['tax'] },
  'tax-amount': { kind: 'computed' },
  'payment': { kind: 'dropdown', optionsKey: 'payment', customType: 'Payment Terms', path: ['payment'] },
  'location': { kind: 'dropdown', optionsKey: 'location', path: ['location'] },
};

// Same sanitising the modal's money inputs use: digits and a single decimal point, no
// negatives.
export const sanitizeMoneyInput = (value) => {
  let sanitized = String(value).replace(/[^0-9.]/g, '');
  const decimalCount = (sanitized.match(/\./g) || []).length;
  if (decimalCount > 1) {
    const parts = sanitized.split('.');
    sanitized = parts[0] + '.' + parts.slice(1).join('');
  }
  sanitized = sanitized.replace(/^-/, '');
  return sanitized === '.' ? '' : sanitized;
};
